import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form, message, Modal } from 'antd';
import BrandedLogo, { isBrandedLogoType } from '../../components/Logo/BrandedLogo';
import poweredByLMI from '../../components/Logo/poweredByLMI.png';
import { headerMainLogoSelector, defaultNewPlanCurrencyCodeSelector } from '../Organisation/OrganisationSelectors';
import PreRegistrationGate from './components/PreRegistrationGate';
import OnboardingOtpVerification from './components/OnboardingOtpVerification';
import OnboardingProgressSteps from './components/OnboardingProgressSteps';
import OnboardingRegistrationStatus from './components/OnboardingRegistrationStatus';
import OnboardingRegistrationStepContent from './components/OnboardingRegistrationStepContent';
import OnboardingStepPanel from './components/OnboardingStepPanel';
import { SUBSCRIPTION_KEY_PLACEHOLDER } from './onboardingConstants';
import { ONBOARDING_STEPS, STEP_FIELDS, STEP_SECTIONS } from './onboardingRegistrationConfig';
import {
  canShowRegistrationForm,
  createOnboardingRegistrationState,
  getOnboardingTagline,
  getRegistrationSuccessDescription,
  isStrictBrokerOnboarding,
  mapOrgUserInvitationResponse,
  applyOrgUserInvitationPrefill,
  shouldShowSkipOrganisation,
  shouldShowSkipPlan,
  shouldSkipAccountEmailVerification,
} from './onboardingRegistrationHelpers';
import {
  runAccountEmailVerification,
  runActivationVerification,
  runOrgUserInvitationValidation,
  runOtpResend,
  runOtpVerification,
  runPreRegistrationActivation,
  submitRegistrationAndSignIn,
} from './onboardingRegistrationFlow';
import { usesSubscriptionActivationVerify } from './onboardingApi';
import { login, completeAuthenticatedSession } from '../../components/Login/LoginAction';
import { fetchBroker } from '../Admin/scenes/Broker/BrokerActions';
import { fetchOrganisations } from '../Organisation/OrganisationAction';
import './OnboardingRegistration.scss';

class OnboardingRegistration extends Component {
  constructor(props) {
    super(props);
    this.state = createOnboardingRegistrationState(props.location.search, props.defaultPlanCurrency);
    this.stepPanelRefs = {};
  }

  componentDidMount() {
    const { preRegistrationComplete, linkParams, registration } = this.state;

    this.runInvitationValidationIfNeeded();

    if (preRegistrationComplete) {
      this.focusActiveStep();
      return;
    }

    if (usesSubscriptionActivationVerify(linkParams)) {
      const { subscriptionKey, promoCode } = registration.activation || {};
      if (String(subscriptionKey || '').trim() || String(promoCode || '').trim()) {
        this.verifyActivationFromRegistration(registration.activation);
      }
    }
  }

  componentDidUpdate(_prevProps, prevState) {
    const stepChanged = prevState.activeStep !== this.state.activeStep;
    const gateUnlocked = !prevState.preRegistrationComplete && this.state.preRegistrationComplete;

    if (stepChanged || (gateUnlocked && this.canShowRegistrationForm())) {
      this.focusActiveStep();
    }

    if (gateUnlocked && this.state.invitationValidationState === 'pending') {
      this.runInvitationValidationIfNeeded();
    }
  }

  canShowRegistrationForm = () => canShowRegistrationForm(this.state);

  runInvitationValidationIfNeeded = () => {
    const { linkParams, invitationValidationState } = this.state;
    if (invitationValidationState !== 'pending') return;

    const invitationUserId = linkParams.userId || linkParams.invitationId;
    if (invitationUserId) {
      this.validateOrgUser(invitationUserId, linkParams.userType);
    }
  };

  validateOrgUser = async (userId, userType) => {
    this.setState({ isValidating: true, validationError: null });

    try {
      const result = await runOrgUserInvitationValidation(userId, userType);

      if (result.ok) {
        const invitationPatch = mapOrgUserInvitationResponse(result.data);

        this.setState(
          ({ registration, linkParams }) => ({
            registration: invitationPatch
              ? applyOrgUserInvitationPrefill(registration, invitationPatch)
              : registration,
            linkParams: invitationPatch?.emailFromApi
              ? { ...linkParams, emailFromLink: true, email: invitationPatch.account?.email || linkParams.email }
              : linkParams,
            preRegistrationComplete: true,
            invitationValidationState: 'valid',
            validationError: null,
          }),
          () => {
            const { form } = this.props;
            const { registration, activeStep } = this.state;
            if (activeStep === 0 && form && registration?.account) {
              form.setFieldsValue({
                email: registration.account.email,
                firstName: registration.account.firstName,
                lastName: registration.account.lastName,
              });
            }
            this.focusActiveStep();
          },
        );
      } else {
        this.setState({
          invitationValidationState: 'invalid',
          validationError: result.error,
        });
      }
    } finally {
      this.setState({ isValidating: false });
    }
  };

  verifyActivationFromRegistration = async (activation = {}) => {
    this.setState({ isVerifyingActivation: true, activationError: null });

    try {
      const result = await runActivationVerification(activation);

      if (result.ok) {
        this.setState({ preRegistrationComplete: true, activationError: null });
        message.success(result.message);
        this.focusActiveStep();
      } else {
        this.setState({ activationError: result.error });
      }
    } finally {
      this.setState({ isVerifyingActivation: false });
    }
  };

  focusActiveStep = () => {
    const { activeStep } = this.state;
    if (!this.canShowRegistrationForm()) return;

    window.setTimeout(() => {
      const panel = this.stepPanelRefs[activeStep];
      if (panel && typeof panel.focus === 'function') {
        panel.focus();
      }
    }, 0);
  };

  onFormSubmit = (e) => {
    e.preventDefault();
  };

  completeStep = (stepIndex) => {
    this.setState(({ completedSteps }) => {
      if (completedSteps.indexOf(stepIndex) >= 0) return null;
      return { completedSteps: [...completedSteps, stepIndex] };
    });
  };

  updateRegistration = (stepIndex, values) => {
    const section = STEP_SECTIONS[stepIndex];
    if (!section) return;

    this.setState(({ registration }) => ({
      registration: {
        ...registration,
        [section]: {
          ...registration[section],
          ...values,
        },
      },
    }));
  };

  validateCurrentStep = (onValid) => {
    const { form } = this.props;
    const { activeStep, linkParams, registration } = this.state;
    const fieldNames = [...STEP_FIELDS[activeStep]];

    if (activeStep === 0 && isStrictBrokerOnboarding(linkParams, registration)) {
      fieldNames.push('brokerCreatingPlan');
    }

    if (!fieldNames.length) {
      this.completeStep(activeStep);
      onValid({});
      return;
    }

    form.validateFields(fieldNames, (err, values) => {
      if (err) return;

      if (activeStep === 0 && values.brokerCreatingPlan !== undefined) {
        const { brokerCreatingPlan, ...accountValues } = values;
        this.setState(({ registration: currentRegistration }) => ({
          registration: {
            ...currentRegistration,
            account: {
              ...currentRegistration.account,
              ...accountValues,
            },
            brokerCreatingPlan: brokerCreatingPlan === 'yes',
          },
        }));
      } else {
        this.updateRegistration(activeStep, values);
      }

      this.completeStep(activeStep);
      onValid(values);
    });
  };

  goToStep = (stepIndex) => {
    const { form } = this.props;
    const { activeStep, completedSteps, preRegistrationComplete } = this.state;

    if (!preRegistrationComplete) return;
    if (stepIndex > activeStep && completedSteps.indexOf(stepIndex - 1) < 0) return;

    this.clearEmailVerificationError();
    form.resetFields();
    this.setState({ activeStep: stepIndex });
    window.scrollTo(0, 0);
  };

  advanceToStep = (stepIndex, { alsoCompleteSteps = [] } = {}) => {
    const { form } = this.props;
    const { activeStep, preRegistrationComplete } = this.state;

    if (!preRegistrationComplete) return;

    form.resetFields();
    this.setState(({ completedSteps }) => {
      const nextCompleted =
        completedSteps.indexOf(activeStep) >= 0 ? [...completedSteps] : [...completedSteps, activeStep];

      alsoCompleteSteps.forEach((step) => {
        if (nextCompleted.indexOf(step) < 0) nextCompleted.push(step);
      });

      return {
        activeStep: stepIndex,
        completedSteps: nextCompleted,
      };
    });
    window.scrollTo(0, 0);
  };

  goBack = () => {
    const { activeStep, registration } = this.state;
    if (activeStep === 0) return;

    this.clearEmailVerificationError();
    if (activeStep === 3 && registration.brokerCreatingPlan === false) {
      this.goToStep(0);
      return;
    }

    this.goToStep(activeStep - 1);
  };

  goNext = () => {
    const { activeStep, preRegistrationComplete, linkParams, registration, invitationValidationState } = this.state;
    if (!preRegistrationComplete) return;

    this.validateCurrentStep((values) => {
      if (
        activeStep === 0 &&
        !shouldSkipAccountEmailVerification(linkParams, invitationValidationState)
      ) {
        this.verifyAccountEmailAndAdvance(values, linkParams, registration);
        return;
      }

      this.advanceFromValidatedStep(activeStep, values, linkParams, registration);
    });
  };

  showEmailVerificationPopup = (result) => {
    if (result.alreadyRegistered) {
      Modal.confirm({
        title: 'This email is already registered',
        content: (
          <div>
            <p>{result.error}</p>
            <p>Please use the standard login page instead of creating a new account.</p>
          </div>
        ),
        okText: 'Go to login',
        cancelText: 'Close',
        onOk: () => {
          this.goToLogin();
        },
      });
      return;
    }

    Modal.error({
      title: 'Email could not be verified',
      content: result.error,
    });
  };

  verifyAccountEmailAndAdvance = async (values, linkParams, registration) => {
    const email = String(values.email ?? '').trim();

    this.setState({ isVerifyingEmail: true, emailVerificationError: null });

    try {
      const result = await runAccountEmailVerification(email);
      if (!result.ok) {
        this.showEmailVerificationPopup(result);

        if (result.alreadyRegistered) {
          this.setState({ emailVerificationError: result.error });
        } else {
          this.props.form.setFields({
            email: { value: email, errors: [new Error(result.error)] },
          });
        }
        return;
      }

      this.advanceFromValidatedStep(0, values, linkParams, registration);
    } finally {
      this.setState({ isVerifyingEmail: false });
    }
  };

  advanceFromValidatedStep = (activeStep, values, linkParams, registration) => {
    if (activeStep === 0 && isStrictBrokerOnboarding(linkParams, registration)) {
      const brokerPlanAnswer = values.brokerCreatingPlan;

      if (brokerPlanAnswer === 'no') {
        this.advanceToStep(3, { alsoCompleteSteps: [1, 2] });
        return;
      }

      if (brokerPlanAnswer === 'yes') {
        this.advanceToStep(1);
        return;
      }
    }

    this.advanceToStep(activeStep + 1);
  };

  clearEmailVerificationError = () => {
    if (this.state.emailVerificationError) {
      this.setState({ emailVerificationError: null });
    }
  };

  goToLogin = () => {
    this.props.history.push('/');
  };

  skipOrganisation = () => {
    const { registration, preRegistrationComplete } = this.state;
    if (!preRegistrationComplete) return;
    this.updateRegistration(1, registration.organisation);
    this.advanceToStep(2);
  };

  skipPlan = () => {
    const { registration, preRegistrationComplete } = this.state;
    if (!preRegistrationComplete) return;
    this.updateRegistration(2, registration.plan);
    this.advanceToStep(3);
  };

  createAccountBeforeOnboarding = () => {
    const { form } = this.props;

    form.validateFields(['subscriptionKey', 'promoCode'], async (err, values) => {
      if (err) return;

      const activation = {
        subscriptionKey: (values.subscriptionKey || '').trim(),
        promoCode: (values.promoCode || '').trim(),
      };

      this.setState({ isVerifyingActivation: true, activationError: null });

      try {
        const result = await runPreRegistrationActivation(activation);

        if (result.ok) {
          this.setState(({ registration }) => ({
            registration: {
              ...registration,
              activation: {
                ...registration.activation,
                ...result.activation,
              },
            },
            preRegistrationComplete: true,
            activationError: null,
          }));

          form.resetFields();
          message.success(result.message);
          window.scrollTo(0, 0);
        } else {
          this.setState({ activationError: result.error });
        }
      } finally {
        this.setState({ isVerifyingActivation: false });
      }
    });
  };

  completeRegistration = () => {
    this.validateCurrentStep(this.submitRegistration);
  };

  submitRegistration = async (values) => {
    const { history, login, completeAuthenticatedSession, fetchBroker, fetchOrganisations } = this.props;
    const { registration, linkParams } = this.state;

    this.setState({ isSubmitting: true });

    const result = await submitRegistrationAndSignIn({
      registration,
      linkParams,
      activationValues: values,
      completeAuthenticatedSession,
      login,
      history,
      fetchBroker,
      fetchOrganisations,
    });

    if (result.ok) {
      if (result.pendingOtpVerification) {
        this.setState({
          registration: result.registration,
          pendingOtpVerification: true,
          otpUserId: result.otpUserId,
          otpResendAvailableAt: result.otpResendAvailableAt,
          otpError: null,
          isActivated: false,
        });
        window.scrollTo(0, 0);
      } else {
        this.setState({
          registration: result.registration,
          isActivated: true,
          pendingOtpVerification: false,
        });
      }
    }

    this.setState({ isSubmitting: false });
  };

  verifyRegistrationOtp = async (otpCode) => {
    const { history, completeAuthenticatedSession, fetchBroker, fetchOrganisations } = this.props;
    const { otpUserId } = this.state;

    this.setState({ isVerifyingOtp: true, otpError: null });

    try {
      const result = await runOtpVerification({
        userId: otpUserId,
        otpCode,
        completeAuthenticatedSession,
        history,
        fetchBroker,
        fetchOrganisations,
      });

      if (result.ok) {
        // Session bootstrap navigates away from /onboarding; only update local UI if still mounted.
        this.setState({
          isActivated: true,
          pendingOtpVerification: false,
          otpError: null,
        });
      } else {
        this.setState({ otpError: result.error });
      }
    } finally {
      this.setState({ isVerifyingOtp: false });
    }
  };

  resendRegistrationOtp = async () => {
    const { registration } = this.state;
    const email = String(registration.account?.email ?? '').trim();
    const password = registration.account?.password ?? '';

    this.setState({ isResendingOtp: true, otpError: null });

    try {
      const result = await runOtpResend({ email, password });

      if (result.ok) {
        this.setState({
          otpResendAvailableAt: result.resendAvailableAt,
          otpError: null,
        });
        message.success(result.message);
      } else {
        this.setState({ otpError: result.error });
      }
    } finally {
      this.setState({ isResendingOtp: false });
    }
  };

  renderStepBody = (stepIndex) => {
    const { form } = this.props;
    const {
      activeStep,
      registration,
      isSubmitting,
      linkParams,
      invitationValidationState,
      isVerifyingEmail,
      emailVerificationError,
    } = this.state;

    if (!this.canShowRegistrationForm()) return null;
    if (invitationValidationState === 'pending' || invitationValidationState === 'invalid') {
      return null;
    }

    return (
      <OnboardingRegistrationStepContent
        stepIndex={stepIndex}
        activeStep={activeStep}
        form={form}
        registration={registration}
        isSubmitting={isSubmitting}
        linkParams={linkParams}
        showSkipOrganisation={shouldShowSkipOrganisation(linkParams, registration)}
        showSkipPlan={shouldShowSkipPlan(linkParams, registration)}
        showBrokerPlanQuestion={isStrictBrokerOnboarding(linkParams, registration)}
        brokerCreatingPlan={registration.brokerCreatingPlan}
        emailFromLink={linkParams.emailFromLink}
        isVerifyingEmail={isVerifyingEmail}
        emailAlreadyRegistered={Boolean(emailVerificationError)}
        emailAlreadyRegisteredMessage={emailVerificationError}
        onClearEmailVerificationError={this.clearEmailVerificationError}
        onGoToLogin={this.goToLogin}
        onContinue={this.goNext}
        onBack={this.goBack}
        onSkipOrganisation={this.skipOrganisation}
        onSkipPlan={this.skipPlan}
        onComplete={this.completeRegistration}
      />
    );
  };

  render() {
    const { logoType, form } = this.props;
    const {
      activeStep,
      completedSteps,
      isActivated,
      registration,
      preRegistrationComplete,
      linkParams,
      isSubmitting,
      invitationValidationState,
      validationError,
      isValidating,
      activationError,
      isVerifyingActivation,
      isVerifyingEmail,
      pendingOtpVerification,
      otpError,
      isVerifyingOtp,
      isResendingOtp,
    } = this.state;
    const showOtpVerification = pendingOtpVerification && !isActivated;
    const showRegistrationForm = this.canShowRegistrationForm() && !showOtpVerification;
    const showPreRegistrationGate =
      !preRegistrationComplete && invitationValidationState === 'not-required' && !showOtpVerification;
    const successDescription = getRegistrationSuccessDescription(linkParams);

    return (
      <main className="onboarding-registration">
        <div className="onboarding-registration-shell">
          <header className="onboarding-registration-header">
            <div className="onboarding-registration-header-bar">
              <BrandedLogo className="onboarding-registration-header-logo" />
              <div className="onboarding-registration-header-copy">
                <p className="onboarding-registration-tagline">{getOnboardingTagline(linkParams)}</p>
              </div>
            </div>
          </header>

          <Form className="onboarding-registration-flow-form" onSubmit={this.onFormSubmit}>
            {showRegistrationForm && (
              <OnboardingProgressSteps
                activeStep={activeStep}
                completedSteps={completedSteps}
                gateLocked={false}
                onStepSelect={this.goToStep}
              />
            )}

            {showPreRegistrationGate && (
              <PreRegistrationGate
                form={form}
                linkParams={linkParams}
                initialActivation={registration.activation}
                isSubmitting={isSubmitting || isVerifyingActivation}
                activationError={activationError}
                onCreateAccount={this.createAccountBeforeOnboarding}
                subscriptionKeyPlaceholder={SUBSCRIPTION_KEY_PLACEHOLDER}
              />
            )}

            {showOtpVerification && (
              <OnboardingOtpVerification
                form={form}
                email={registration.account?.email}
                otpError={otpError}
                isVerifying={isVerifyingOtp}
                isResending={isResendingOtp}
                onVerify={this.verifyRegistrationOtp}
                onResend={this.resendRegistrationOtp}
              />
            )}

            <OnboardingRegistrationStatus
              isVerifyingActivation={isVerifyingActivation}
              isVerifyingEmail={isVerifyingEmail}
              activationError={activationError}
              showPreRegistrationGate={showPreRegistrationGate}
              showRegistrationForm={showRegistrationForm}
              isValidating={isValidating}
              validationError={validationError}
              isActivated={isActivated}
              successTitle="Registration complete"
              successDescription={successDescription}
            />

            {showRegistrationForm && (
              <div className="onboarding-registration-card">
                {ONBOARDING_STEPS.map((step, index) => (
                  <OnboardingStepPanel
                    ref={(instance) => {
                      this.stepPanelRefs[index] = instance;
                    }}
                    activeStep={activeStep}
                    completedSteps={completedSteps}
                    gateLocked={false}
                    index={index}
                    key={step.title}
                    onSelect={this.goToStep}
                    step={step}
                  >
                    {this.renderStepBody(index)}
                  </OnboardingStepPanel>
                ))}
              </div>
            )}
          </Form>

          {isBrandedLogoType(logoType) && (
            <a
              className="onboarding-registration-powered-by"
              href="https://www.lmigroup.io"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={poweredByLMI} alt="Powered by LMI Group" />
            </a>
          )}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state) => ({
  logoType: headerMainLogoSelector(state),
  defaultPlanCurrency: defaultNewPlanCurrencyCodeSelector(state),
});

const mapDispatchToProps = { login, completeAuthenticatedSession, fetchBroker, fetchOrganisations };

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(OnboardingRegistration));
